import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static values = {
    variant: String,
    data: Object
  }

  static targets = ["chart"]

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  renderChart() {
    const data = this.dataValue
    if (!data) return

    if (this.variantValue === "stacked_bar") {
      this.renderStackedBar(data)
    } else if (this.variantValue === "line") {
      this.renderLine(data)
    } else if (this.variantValue === "total") {
      this.renderTotal(data)
    } else if (this.variantValue === "funnel") {
      this.renderFunnel(data)
    } else if (this.variantValue === "monthly_engagement") {
      this.renderMonthlyEngagement(data)
    }
  }

  switchToBar() {
    this.variantValue = "stacked_bar"
    this.updateToggleUI("bar")
    if (this.chart) this.chart.destroy()
    this.renderChart()
  }

  switchToLine() {
    this.variantValue = "line"
    this.updateToggleUI("line")
    if (this.chart) this.chart.destroy()
    this.renderChart()
  }

  switchToTotal() {
    this.variantValue = "total"
    this.updateToggleUI("total")
    if (this.chart) this.chart.destroy()
    this.renderChart()
  }

  updateToggleUI(active) {
    const activeClasses = "bg-white text-support-dark-700 shadow-sm font-medium"
    const inactiveClasses = "text-support-dark-500"

    const buttons = {
      bar: this.element.querySelector(".moet-chart-toggle-bar"),
      line: this.element.querySelector(".moet-chart-toggle-line"),
      total: this.element.querySelector(".moet-chart-toggle-total")
    }

    Object.entries(buttons).forEach(([name, btn]) => {
      if (!btn) return
      const state = name === active ? activeClasses : inactiveClasses
      btn.className = `moet-chart-toggle-${name} text-xs px-2 py-1 rounded-md ${state}`
    })
  }

  renderStackedBar(data) {
    const colors = [
      "#734CE0", "#E08C4C", "#4CADE0", "#E04C7A",
      "#4CE0A8", "#A84CE0", "#E0C84C", "#4C7AE0",
      "#E04CA8", "#7AE04C"
    ]

    const options = {
      chart: {
        type: "bar",
        stacked: true,
        height: 320,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: data.series || [],
      colors: colors.slice(0, (data.series || []).length),
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          columnWidth: "60%"
        }
      },
      xaxis: {
        categories: data.weeks || [],
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: {
        position: "right",
        fontSize: "12px",
        labels: { colors: "#6B7280" },
        itemMargin: { vertical: 4 }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val) => val.toLocaleString() }
      },
      dataLabels: { enabled: false }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  renderLine(data) {
    const colors = [
      "#734CE0", "#E08C4C", "#4CADE0", "#E04C7A",
      "#4CE0A8", "#A84CE0", "#E0C84C", "#4C7AE0",
      "#E04CA8", "#7AE04C"
    ]

    const seriesCount = (data.series || []).length
    const dashPatterns = [0, 5, 8, 3, 10, 4, 6, 12, 7, 9]
    const markerShapes = ["circle", "square", "diamond", "triangle", "star",
                          "circle", "square", "diamond", "triangle", "star"]

    const options = {
      chart: {
        type: "line",
        height: 320,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: data.series || [],
      colors: colors.slice(0, seriesCount),
      stroke: {
        width: 3,
        curve: "smooth",
        dashArray: dashPatterns.slice(0, seriesCount)
      },
      markers: {
        size: 5,
        strokeWidth: 0,
        shape: markerShapes.slice(0, seriesCount)
      },
      xaxis: {
        categories: data.weeks || [],
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: {
        position: "right",
        fontSize: "12px",
        labels: { colors: "#6B7280" },
        markers: { shape: markerShapes.slice(0, seriesCount) },
        itemMargin: { vertical: 4 }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val) => val.toLocaleString() }
      },
      dataLabels: { enabled: false }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  renderTotal(data) {
    const weeks = data.weeks || []
    const seriesList = data.series || []
    const totals = weeks.map((_, i) =>
      seriesList.reduce((sum, s) => sum + ((s.data && s.data[i]) || 0), 0)
    )

    const options = {
      chart: {
        type: "line",
        height: 320,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: [{ name: "Total MOETs", data: totals }],
      colors: ["#734CE0"],
      stroke: { width: 3, curve: "smooth" },
      markers: { size: 5, strokeWidth: 0, shape: "circle" },
      xaxis: {
        categories: weeks,
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: { show: false },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val) => val.toLocaleString() }
      },
      dataLabels: { enabled: false }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  renderMonthlyEngagement(data) {
    const colors = ["#734CE0", "#E08C4C", "#4CADE0"]
    const dashPatterns = [0, 5, 8]
    const markerShapes = ["circle", "square", "diamond"]
    const series = data.series || []

    const options = {
      chart: {
        type: "line",
        height: 340,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: series,
      colors: colors.slice(0, series.length),
      stroke: {
        width: 3,
        curve: "smooth",
        dashArray: dashPatterns.slice(0, series.length)
      },
      markers: {
        size: 5,
        strokeWidth: 0,
        shape: markerShapes.slice(0, series.length)
      },
      xaxis: {
        categories: data.months || [],
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          style: { colors: "#9CA3AF", fontSize: "11px" },
          formatter: (val) => `${Math.round(val)}%`
        }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: {
        position: "top",
        horizontalAlign: "left",
        fontSize: "12px",
        labels: { colors: "#6B7280" },
        markers: { shape: markerShapes.slice(0, series.length) },
        itemMargin: { horizontal: 10, vertical: 4 }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val) => (val == null ? "—" : `${val}%`) }
      },
      dataLabels: { enabled: false }
    }

    const target = data.target
    if (target != null) {
      options.annotations = {
        yaxis: [
          {
            y: target,
            strokeDashArray: 0,
            borderColor: "#000000",
            borderWidth: 2,
            label: {
              text: `Target ${target}%`,
              borderColor: "#000000",
              position: "right",
              textAnchor: "end",
              style: { color: "#fff", background: "#000000", fontSize: "11px" }
            }
          }
        ]
      }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  renderFunnel(data) {
    const stages = ["Engaged", "Submitted", "Completed", "Approved"]
    const counts = [
      data.engaged || 0,
      data.submitted || 0,
      data.completed || 0,
      data.approved || 0
    ]
    // Step conversion: each stage as a % of the previous stage. The first
    // (Engaged) stage is shown as a % of all workable claims (data.total).
    const stepBase = (i) => (i === 0 ? (data.total || 0) : counts[i - 1])
    const stepLabel = (i) => (i === 0 ? "Total Claims" : stages[i - 1])
    const stepPct = (i) => {
      const base = stepBase(i)
      if (!base) return null
      return Math.round((counts[i] / base) * 1000) / 10
    }

    const options = {
      chart: {
        type: "bar",
        height: 200,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: [{ name: "Claims", data: counts }],
      colors: ["#734CE0"],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "60%"
        }
      },
      xaxis: {
        categories: stages,
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } }
      },
      yaxis: {
        labels: { style: { fontSize: "12px", colors: "#374151" } }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const pct = stepPct(opts.dataPointIndex)
          return pct == null ? val.toLocaleString() : `${val.toLocaleString()} (${pct}%)`
        },
        style: { fontSize: "12px", colors: ["#fff"] }
      },
      tooltip: {
        y: {
          formatter: (val, opts) => {
            const i = opts.dataPointIndex
            const pct = stepPct(i)
            return pct == null
              ? val.toLocaleString()
              : `${val.toLocaleString()} — ${pct}% of ${stepLabel(i)}`
          }
        }
      }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }
}
